// useMyChats (issue #419) — caller's chat inbox.
//
// Reads `GET /chats/me`. Supports `?type=` and `?jobId=` filters at the
// hook level so the Messenger 2.0 tabs (#420) and the job-detail panel
// (#421) reuse the same hook with different params.

import { readChatActorId } from 'src/auth/sessionEvents';

import { useQuery, UseQueryOptions } from '@tanstack/react-query';

import { getMyChats, MyChatsFilter } from './api';
import { chatKeys } from './keys';

import type { Chat } from 'src/models/chat';

export type MyChatsFilters = MyChatsFilter & {
  /**
   * Per-participant archive filter applied client-side. The server returns
   * every chat the caller participates in; the inbox UI flips between
   * archived and unarchived buckets on the client because the server has
   * no `?archived=` query (and adding one would require a backend change).
   */
  archived?: boolean;
};

export function useMyChats(
  filter: MyChatsFilters = {},
  options?: UseQueryOptions<Chat[]>
) {
  const { archived, ...serverFilter } = filter;
  return useQuery<Chat[]>(
    chatKeys.myChats(filter),
    async () => {
      const page = await getMyChats(serverFilter);
      const rows = page.data ?? [];
      if (typeof archived !== 'boolean') return rows;
      const currentUserId = readChatActorId();
      // Filter by the caller's own `ChatParticipants.archivedAt`. The row
      // is bundled in two possible shapes: either flat (`isArchived`) or
      // nested in `participants`. Since the server now returns *all*
      // participants, find the caller's row by `userId` rather than
      // assuming `participants[0]`. An explicit caller timestamp or null wins
      // over the legacy flat flag. An operational thread with no caller row
      // must not borrow another participant's archive state.
      return rows.filter((row) => {
        const fromFlat = row.isArchived;
        const myParticipant =
          row.participants &&
          row.participants.find((p) => p.userId === currentUserId);
        const fromParticipant = myParticipant?.archivedAt !== undefined
          ? !!myParticipant.archivedAt
          : undefined;
        const flagged =
          typeof fromParticipant === 'boolean'
            ? fromParticipant
            : typeof fromFlat === 'boolean'
            ? fromFlat
            : false;
        return archived ? flagged : !flagged;
      });
    },
    options
  );
}
